//* socket.io
import io, { Socket } from "socket.io-client";
//* 组件库
import { $loading, $message, type UiTextarea } from "various-ui";
//* 公共组件
import type { ConfirmModal } from "@/components/windows";
//* 公共函数
import { Formats, Systems } from "@/assets/js";
//* 样式
import css_normalize from "@/assets/style/src/index.module.less";

export const useComposable = () => {
    //* 获取全局变量
    const config = useRuntimeConfig();
    //* 插件
    const i18n = useI18n();
    const route = useRoute();

    //* 静态属性
    const vars = {
        timer: <NodeJS.Timeout | undefined>undefined,
        socket: <Socket | undefined>undefined,
        welcome: false,
    };

    //* 响应式属性
    const refs = {
        id: ref<string>(Formats.query(route.query.id)),
        name: ref<string>(""),
        keyword: ref<string>(""),
        visible: ref<boolean>(false),
        messages: ref<any[]>([]),
        historys: ref<{ id: string; name: string; date: string }[]>([]),
    };

    //* 数据节点
    const nodes = {
        textarea: ref<InstanceType<typeof UiTextarea>>(),
        confirm: ref<InstanceType<typeof ConfirmModal>>(),
        content: ref<HTMLDivElement>(),
    };

    //* 计算属性
    const computeds = {
        //* socket链接
        socketURL: computed(() => {
            if (location && location.protocol == "https:") return `https://${config.public.SOCKET_DOMAIN}`;
            else {
                return `http://${config.public.SOCKET_DOMAIN}`;
            }
        }),

        //* 是否禁用输入窗口提交
        readonly: computed(() => {
            const result = refs.messages.value?.slice(-1)?.[0];
            if (result?.msg_type == "answer" && result?.msg_code == "readonly") return true;
            else {
                return false;
            }
        }),
    };

    //* socket处理函数
    const socket = {
        //* 字符串解析
        analyze: (context?: string) => {
            //* 正则表达式，用于匹配大多数常见的URL
            const regex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
            //* 替换匹配的URL为HTML链接
            return context?.replace(regex, function (url) {
                //* 确保URL是有效的（避免匹配了非URL的文本片段）
                //* 这里只是一个简单的检查，实际中可能需要更复杂的逻辑
                if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("ftp://") || url.startsWith("file://")) {
                    return `<a href="${url}" class="${css_normalize.assets}" target="_blank">${url}</a>`;
                }

                //* 如果不是URL，则返回原始文本
                return url;
            });
        },

        //* 接收websocket信息
        message: (data: any) => {
            //* 数据更新...
            if (data.msg_type == "answer" || data.msg_type == "form") {
                //* 数据初始化
                const index = refs.messages.value.length - 1;
                //* 数据更新
                refs.messages.value[index] = Object.assign(refs.messages.value[index], data);
                refs.messages.value[index].message = socket.analyze(refs.messages.value[index].message);
                //* 信息滚动到底部
                nextTick(() => {
                    if (nodes.content.value) {
                        nodes.content.value?.scrollTo({
                            top: nodes.content.value.scrollHeight - nodes.content.value.offsetHeight,
                            behavior: "smooth",
                        });
                    }
                });
            }
        },

        //* 发送websocket信息
        send: (data: any, type: "user" | "answer") => {
            //* 数据更新
            if (type == "user") {
                refs.keyword.value = "";
                refs.messages.value.push(data);
            }

            //* 机器人加载中占位数据
            refs.messages.value.push({ msg_type: "answer", msg_code: "readonly" });

            //* 检测websocket是否需要重连
            if (!vars.socket) methods.connect(() => vars.socket?.emit("send", data));
            else if (vars.socket.connected) vars.socket?.emit("send", data);
            else {
                //* 重连并发送数据包
                vars.socket.connect().emit("send", data);
            }

            //* 信息滚动到底部
            nextTick(() => {
                if (nodes.content.value) {
                    nodes.content.value?.scrollTo({
                        top: nodes.content.value.scrollHeight - nodes.content.value.offsetHeight,
                        behavior: "smooth",
                    });
                }
            });
        },
    };

    //* 函数列表
    const methods = {
        //* 连接函数
        connect: (callBack?: () => void) => {
            //* 启动加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });

            //* 创建socket.io连接
            vars.socket = io(computeds.socketURL.value, {
                path: config.public.SOCKET_CHATAGENT_PATH,
                transports: ["websocket", "polling"],
                autoConnect: false,
                query: {
                    user_id: get_canvas_fingerprint(),
                    session_id: refs.id.value,
                },
            });

            //* 接受服务器返回的数据
            vars.socket.on("send", (event) => socket.message(event));
            //* socket连接失败回调
            vars.socket.on("error", (error) => $message.error(error?.message || error));
            //* socket连接关闭回调
            vars.socket.on("disconnect", () => {
                vars.timer && clearInterval(vars.timer);
                vars.timer = undefined;
            });

            //* socket连接成功回调
            vars.socket.on("connect", () => {
                //* 获取用户的历史记录
                api.frontend("/api/chatagent/historys", {
                    hidden: true,
                    data: { id: get_canvas_fingerprint() },
                    error: () => (refs.historys.value = []),
                    success: (response) => (refs.historys.value = response || []),
                    finally: () => {
                        //* 数据更新
                        refs.keyword.value = "";
                        if (vars.welcome) {
                            vars.welcome = false;
                            refs.messages.value.push({ msg_type: "answer", msg_code: "connect", message: i18n.t("ChatagentGreeting") });
                        }

                        //* 文本框获取焦点
                        nodes.textarea.value?.focus();
                        //* 注册心跳机制
                        vars.timer && clearInterval(vars.timer);
                        vars.timer = setInterval(() => vars.socket?.emit("ping", { session_id: refs.id.value }), 5000);
                        //* 关闭加载器
                        $loading.close();
                        //* 事件回调
                        callBack && callBack();
                    },
                });
            });

            //* 连接
            vars.socket.connect();
        },

        //* 历史记录
        history: (data: { id: string; name: string; date: string }) => {
            if (data.id != refs.id.value) {
                //* 启动加载器
                $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
                //* 接口请求
                api.frontend("/api/chatagent/history", {
                    data: { id: get_canvas_fingerprint(), history: data.id },
                    finally: () => $loading.close(),
                    success: (response) => {
                        //* 数据初始化
                        const result = response?.map((value: any) => {
                            return Object.assign(value, { message: socket.analyze(value?.message) });
                        });

                        //* 数据更新
                        refs.name.value = data.name;
                        //* 新建一个websocket
                        methods.create(data.id, result || []);
                    },
                });
            }
        },

        //* 不喜欢消息
        dislike: (data: any) => {
            //* 启动加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            //* 接口请求
            api.frontend("/api/chatagent/like", {
                data: { id: data.msg_id, type: "dislike", value: !data.dislike },
                method: "POST",
                finally: () => $loading.close(),
                success: () => {
                    data.like = false;
                    data.dislike = !data.dislike;
                },
            });
        },

        //* 提交函数
        submit: (data: any) => {
            //* 数据格式化
            const result = { lang: i18n.locale.value, user_uid: get_canvas_fingerprint(), msg_type: "form-submit", session_id: refs.id.value };
            //* 数据推送
            socket.send(Object.assign(result, data), "answer");
        },

        //* 删除会话
        remove: (data: any) => {
            //* 启动加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            //* 接口请求
            api.frontend("/api/chatagent/history", {
                data: { id: get_canvas_fingerprint(), history: data.id },
                method: "DELETE",
                finally: () => $loading.close(),
                success: () => {
                    $message.success(i18n.t("ChatagentRemoveSuccess")); //* 消息提示
                    if (refs.id.value == data.id) {
                        methods.create();
                    } else {
                        refs.historys.value = refs.historys.value.filter((value) => value.id != data.id);
                    }
                },
            });
        },

        //* 新建会话
        create: (id?: string, messages?: any[]) => {
            //* 数据初始化
            refs.id.value = id || gainID();
            refs.keyword.value = "";
            refs.messages.value = messages || [];
            //* 检测是否需要显示欢迎语
            if (!id) {
                vars.welcome = true;
                refs.name.value = "";
            }

            //* 检测是否需要断开上一个socket链接
            if (vars.socket) {
                vars.socket.disconnect();
            }

            //* 连接会话
            methods.connect();
        },

        //* 喜欢消息
        like: (data: any) => {
            //* 启动加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            //* 接口请求
            api.frontend("/api/chatagent/like", {
                data: { id: data.msg_id, type: "like", value: !data.like },
                method: "POST",
                finally: () => $loading.close(),
                success: () => {
                    data.like = !data.like;
                    data.dislike = false;
                },
            });
        },

        //* 文本复制
        copy: (message: string) => Systems.copyText(message),
        //* 发送函数
        send: () => {
            //* 数据格式化
            const keyword = refs.keyword.value.trim();
            const result = {
                lang: i18n.locale.value,
                user_uid: get_canvas_fingerprint(),
                session_id: refs.id.value,
                msg_type: "ask",
                message: keyword,
            };

            //* 检测是否允许发送信息
            if (!keyword || computeds.readonly.value) return;
            else {
                socket.send(result, "user");
            }
        },
    };

    //* 动态属性
    const binds = reactive({
        input: computed(() => {
            return {
                height: 88,
                maxlength: 500,
                placeholder: i18n.t("AskMeAbout"),
            };
        }),
    });

    return { vars, refs, nodes, binds, methods, computeds };
};
